import React from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import Iridescence from './blocks/Backgrounds/Iridescence/Iridescence';
import SimpleRotatingText from './blocks/TextAnimations/SimpleRotatingText/SimpleRotatingText';
import GradientText from './blocks/TextAnimations/GradientText/GradientText';
import ScrollFloat from './blocks/TextAnimations/ScrollFloat/ScrollFloat';
import AppleIcon from './components/AppleIcon';
import AnimatedContent from './blocks/Animations/AnimatedContent/AnimatedContent';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import TouristCounter from './components/TouristCounter';
import LogoScroller from './components/LogoScroller';
import SpotlightCard from './blocks/Components/SpotlightCard/SpotlightCard';
import TestimonialCarousel from './components/TestimonialCarousel';
import FeatureRow from './components/FeatureRow';
import { testimonialGroups } from './data/testimonials';

// SVG Check component
const CheckIcon = () => (
  <svg 
    className="check-icon" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M5 13L9 17L19 7" 
      stroke="#00C853" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

function App() {
  return (
    <Routes>
      <Route path="/" element={
        <div className="App">
          <Header />
          <section id="hero" className="hero-section">
            <div className="hero-background">
              <Iridescence
                color={[1, 1, 1]}
                mouseReact={false}
                amplitude={0.2}
                speed={0.3}
                gradient={true}
              />
            </div>
            <div className="hero-layout">
              <div className="hero-content">
                <AnimatedContent
                  distance={150}
                  direction="vertical"
                  reverse={false}
                  config={{ tension: 80, friction: 20 }}
                  initialOpacity={0.2}
                  animateOpacity
                  scale={1}
                  threshold={0.1}
                >
                  <h1 className="single-line-heading">The world's first AI Tour Guide</h1>
                  <div className="hero-subtext">
                    <p>Unfold stories behind</p>
                    <span className="highlight-text">
                      <SimpleRotatingText 
                        texts={[
                          "🎨 art ",
                          "🗿 statues ",
                          "🖼️ paintings ",
                          "🏛️ architecture ",
                          "🗼 landmarks ",
                          "🏛️ museums ",
                          "🏰 castles ",
                          "⛩️ temples ",
                          "👑 palaces ",
                          "⛪ cathedrals ",
                          "🏺 ancient ruins ",
                          "📜 historical sites "
                        ]}
                        speed={2000}
                      />
                    </span>
                  </div>
                  
                  <div className="hero-buttons">
                    <a href="https://testflight.apple.com/join/r6bUD8q1" target="_blank" rel="noopener noreferrer" className="primary-button">Get it on App Store</a>
                    <button className="secondary-button">Join Android waitlist</button>
                  </div>
                  
                  <div className="hero-subtext">
                    <p className="subtitle">Art should speak to everyone, not just experts.</p>
                  </div>
                </AnimatedContent>
              </div>
              
              <div className="hero-video">
                <AnimatedContent
                  distance={100}
                  direction="vertical"
                  reverse={false}
                  config={{ tension: 80, friction: 20 }}
                  initialOpacity={0.2}
                  animateOpacity
                  scale={1}
                  threshold={0.1}
                >
                  <div className="video-container">
                    <iframe 
                      width="320" 
                      height="700"
                      src="https://www.youtube.com/embed/uSj7wIEPjgk?vq=hd1080&hd=1"
                      title="Demo Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    ></iframe>
                  </div>
                </AnimatedContent>
              </div>
            </div>
          </section>
          
          {/* Tourist counter and logo scroller */}
          <section className="cta-section">
            <TouristCounter count={1574} />
            <LogoScroller />
          </section>
          
          <main>
            <section id="features" className="section">
              <ScrollFloat containerClassName="section-heading-container" textClassName="section-heading">With SeeM, you can...</ScrollFloat>
              <div className="features-container">
                <FeatureRow 
                  image="/images/stock/scan_tag.png"
                  alt="Scan & Discover"
                  title="Scan & Discover"
                  description="Your journey into rich, intelligent art insights begins by simply scanning the museum label next to an artwork that captures your interest."
                  imageOnLeft={true}
                />
                
                <FeatureRow 
                  image="/images/stock/artwork_cards.png"
                  alt="Curated Insights"
                  title="Curated Insights"
                  description="You'll instantly unlock 3 curated info cards detailing: the artwork's content and meaning, the artist's background, and the broader historical or artistic movement it belongs to. All sources are transparently cited and linked. We work closely with museum docents and curators to ensure information is trustworthy, accurate, and well-researched."
                  imageOnLeft={false}
                />
                
                <FeatureRow 
                  image="/images/stock/audio_guide.png"
                  alt="AI Audio Guide"
                  title="AI Audio Guide"
                  description="Our AI Audio Guide brings these insights to life through vivid, story-driven narration designed to spark curiosity and emotional connection. Trained on thousands of samples from professional art narrators, trusted podcasters, and official museum content, it delivers natural, engaging audio in multiple languages and at various depth levels—far beyond what a single human guide could offer."
                  imageOnLeft={true}
                />
                
                <FeatureRow 
                  image="/images/stock/tour_guide.png"
                  alt="Interactive Q&A"
                  title="Interactive Q&A"
                  description="For deeper engagement, you can ask AI Tour Guide any question via text or voice. Above the input box, we offer suggested prompts to help you get started. Responses are tailored, conversational, and informed by your interests."
                  imageOnLeft={false}
                />
                
                <FeatureRow 
                  image="/images/stock/journal.png"
                  alt="Personal Journal"
                  title="Personal Journal"
                  description="We also serve as your Personal Visiting Journal. Keep track of your journey by saving favorite artworks, insights, and adding personal notes—all in one place! Your visits are more than memories; they become evolving reflections of your learning and taste."
                  imageOnLeft={true}
                />
                
                <FeatureRow 
                  image="/images/stock/summary.png"
                  alt="Visit Summary"
                  title="Visit Summary"
                  description="At the end of each visit, you will receive a personalized summary including: time spent, favorite pieces, recurring themes, and more. This wrap-up turns each museum trip into a memorable, shareable, and educational experience."
                  imageOnLeft={false}
                />
              </div>
            </section>
            
            <section id="pricing" className="section pricing-section">
              <ScrollFloat containerClassName="section-heading-container" textClassName="section-heading">Start your journey for free</ScrollFloat>
              <div className="pricing-container">
                <SpotlightCard className="pricing-card">
                  <div className="pricing-card-content">
                    <h3 className="plan-name">Free Trial</h3>
                    <div className="plan-price">Free</div>
                    <p className="plan-description">Explore essential features to start your journey.</p>
                    
                    <ul className="feature-list">
                      <li><CheckIcon /> Multi-language support</li>
                      <li><CheckIcon /> Smart Recommendations</li>
                      <li><CheckIcon /> Tour up to 10 artworks</li>
                      <li><CheckIcon /> Up to 10 AI Audio Guide</li>
                      <li><CheckIcon /> AI Tour Guide – 2 questions per artwork</li>
                      <li><CheckIcon /> Save up to 10 favorites</li>
                    </ul>
                  </div>
                </SpotlightCard>

                <SpotlightCard className="pricing-card">
                  <div className="pricing-card-content">
                    <h3 className="plan-name">Starter</h3>
                    <div className="plan-price">$4<span className="price-period">/hour</span></div>
                    <p className="plan-description">Perfect for focused visitors seeking a rich experience.</p>
                    
                    <ul className="feature-list">
                      <li><CheckIcon /> Multi-language support</li>
                      <li><CheckIcon /> Smart Recommendations</li>
                      <li><CheckIcon /> Unlimited artwork tours</li>
                      <li><CheckIcon /> Unlimited AI Audio Guide</li>
                      <li><CheckIcon /> Customizable AI Tour Guide – unlimited questions</li>
                      <li><CheckIcon /> Save unlimited favorites</li>
                      <li><CheckIcon /> Personal notes & photo uploads</li>
                    </ul>
                  </div>
                </SpotlightCard>

                <SpotlightCard className="pricing-card spotlight-highlighted">
                  <div className="pricing-card-content">
                    <h3 className="plan-name">Premium</h3>
                    <div className="plan-price">$10<span className="price-period">/day</span></div>
                    <p className="plan-description">Ideal for curious minds craving the ultimate experience.</p>
                    
                    <ul className="feature-list">
                      <li><CheckIcon /> Multi-language support</li>
                      <li><CheckIcon /> Smart Recommendations</li>
                      <li><CheckIcon /> Unlimited artwork tours</li>
                      <li><CheckIcon /> Unlimited AI Audio Guide</li>
                      <li><CheckIcon /> Customizable AI Tour Guide – unlimited questions</li>
                      <li><CheckIcon /> Save unlimited favorites</li>
                      <li><CheckIcon /> Personal notes & photo uploads</li>
                      <li><CheckIcon /> Customized visiting Summary at the end of the trip</li>
                    </ul>
                  </div>
                </SpotlightCard>
                
                
              </div>
            </section>
            
            {/* Testimonial Section */}
            <section id="testimonials" className="section testimonials-section">
              <ScrollFloat containerClassName="section-heading-container" textClassName="section-heading">See what our users are saying</ScrollFloat>
              <TestimonialCarousel 
                testimonialGroups={testimonialGroups}
                velocities={[30, -25, 20]}
              />
            </section>
            
          </main>

          {/* Download CTA Section */}
          <section className="download-cta-section">
            <div className="download-cta-container">
              <div className="download-cta-image">
                <img src="/images/stock/product.png" alt="AI Tour Guide App" />
              </div>
              <div className="download-cta-content">
                <h2 className="download-cta-heading">
                  Transforms your visits into personalized, interactive, and intelligent art insights.
                </h2>
                <div className="download-cta-button">
                  <a href="https://testflight.apple.com/join/r6bUD8q1" target="_blank" rel="noopener noreferrer" className="primary-button">
                    Get it on App Store
                  </a>
                </div>
              </div>
            </div>
          </section>
          
          <Footer />
        </div>
      } />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/cookies" element={<CookiePolicy />} />
    </Routes>
  );
}

export default App;
